import { ReloadOutlined } from "@ant-design/icons";
import Typography from "antd/lib/typography";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { type INotificationResource } from "@sol/sdk";
import { DoubleCheck } from "@sol/uikit/core/icons/General/DoubleCheck";
import { Box } from "@sol/uikit/core/icons/TravelAndLocation/Box";
import { Empty } from "@sol/uikit/data-display/Empty/Empty";
import { Popconfirm } from "@sol/uikit/feedback/Popconfirm/Popconfirm";
import { Result } from "@sol/uikit/feedback/Result/Result";
import { Skeleton } from "@sol/uikit/feedback/Skeleton/Skeleton";
import { Spin } from "@sol/uikit/feedback/Spin/Spin";
import { Button } from "@sol/uikit/general/Button/Button";

import { NotificationItem } from "@/entities/notification";
import { UserAvatar } from "@/entities/user";

import { NotificationActions } from "./NotificationActions";
import { useOnVisible } from "../model/useOnVisible";

const SKELETON_ROWS = [1, 2, 3];

// The next row moves up into place, the previous one takes over at the end of the list
const getRowTakingFocusAfterRemoval = (index: number, rowCount: number) =>
    index + 1 < rowCount ? index + 1 : index - 1;

type Props = {
    notifications: INotificationResource[];
    isLoading: boolean;
    hasLoadError: boolean;
    hasNextPageError: boolean;
    onReload: () => unknown;
    isReloading: boolean;
    canFetchNextPage: boolean;
    isFetchingNextPage: boolean;
    onReachEnd: () => unknown;
    hasUnread: boolean;
    isMarkingAllAsRead: boolean;
    onMarkAllAsRead: () => void;
    hasRead: boolean;
    isArchivingRead: boolean;
    onArchiveRead: () => void;
    onOpenNotification: (notification: INotificationResource) => void;
    onMarkNotificationAsRead: (notification: INotificationResource) => void;
    onArchiveNotification: (notification: INotificationResource) => void;
};

export const NotificationPanel = ({
    notifications,
    isLoading,
    hasLoadError,
    hasNextPageError,
    onReload,
    isReloading,
    canFetchNextPage,
    isFetchingNextPage,
    onReachEnd,
    hasUnread,
    isMarkingAllAsRead,
    onMarkAllAsRead,
    hasRead,
    isArchivingRead,
    onArchiveRead,
    onOpenNotification,
    onMarkNotificationAsRead,
    onArchiveNotification,
}: Props) => {
    const [t] = useTranslation();
    const sectionRef = useRef<HTMLElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const endRef = useRef<HTMLLIElement>(null);

    useEffect(() => {
        sectionRef.current?.focus();
    }, []);

    useOnVisible(endRef, listRef, {
        isEnabled: canFetchNextPage,
        onVisible: onReachEnd,
    });

    // Row actions unmount the focused button, so focus moves to a row that stays, or to the panel
    const focusRow = (index: number) =>
        (listRef.current?.children[index]?.querySelector("button") ?? sectionRef.current)?.focus();

    const markAsRead = (notification: INotificationResource, index: number) => {
        focusRow(index);
        onMarkNotificationAsRead(notification);
    };

    const archive = (notification: INotificationResource, index: number) => {
        focusRow(getRowTakingFocusAfterRemoval(index, notifications.length));
        onArchiveNotification(notification);
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex flex-col gap-ant px-ant py-ant-sm">
                    {SKELETON_ROWS.map(row => (
                        <Skeleton key={row} active avatar paragraph={{ rows: 1 }} title={false} />
                    ))}
                </div>
            );
        }

        if (hasLoadError) {
            return (
                <Result
                    status="error"
                    title={t("navbar.notificationsLoadError")}
                    extra={
                        <Button icon={<ReloadOutlined />} onClick={() => onReload()}>
                            {t("navbar.retry")}
                        </Button>
                    }
                />
            );
        }

        if (notifications.length === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("navbar.noNotifications")}
                    className="py-ant-lg"
                />
            );
        }

        // 160px leaves room for the panel offset, header and footer on short screens
        return (
            <ul
                ref={listRef}
                className="m-0 max-h-[min(420px,calc(100dvh-160px))] list-none divide-y divide-ant-secondary overflow-y-auto p-0"
            >
                {notifications.map((notification, index) => (
                    <li key={notification["@id"]}>
                        <NotificationItem
                            notification={notification}
                            avatar={<UserAvatar user={notification.senderUser} />}
                            actions={
                                <NotificationActions
                                    notification={notification}
                                    onMarkAsRead={() => markAsRead(notification, index)}
                                    onArchive={() => archive(notification, index)}
                                />
                            }
                            onOpen={onOpenNotification}
                        />
                    </li>
                ))}
                {hasNextPageError && (
                    <li className="flex justify-center py-ant-xs">
                        <Button type="link" icon={<ReloadOutlined />} loading={isReloading} onClick={() => onReload()}>
                            {t("navbar.retry")}
                        </Button>
                    </li>
                )}
                <li ref={endRef} aria-hidden className="flex justify-center border-none">
                    {isFetchingNextPage && <Spin size="small" className="py-ant-sm" />}
                </li>
            </ul>
        );
    };

    return (
        <section
            ref={sectionRef}
            tabIndex={-1}
            aria-labelledby="notifications-title"
            aria-busy={isLoading}
            className="flex w-[min(360px,calc(100vw-32px))] flex-col outline-none"
        >
            <header className="flex items-center justify-between gap-ant-sm border-b border-ant-secondary px-ant py-ant-sm">
                <Typography.Text id="notifications-title" strong>
                    {t("navbar.notificationsTitle")}
                </Typography.Text>
                <Button
                    type="link"
                    size="small"
                    icon={<DoubleCheck size={16} />}
                    disabled={!hasUnread}
                    loading={isMarkingAllAsRead}
                    onClick={onMarkAllAsRead}
                    className="p-0"
                >
                    {t("navbar.markAllAsRead")}
                </Button>
            </header>
            {renderContent()}
            {notifications.length > 0 && (
                <footer className="flex justify-end border-t border-ant-secondary px-ant py-ant-xs">
                    <Popconfirm
                        title={t("navbar.archiveReadConfirmTitle")}
                        description={t("navbar.archiveReadConfirmDescription")}
                        okText={t("navbar.archiveReadConfirmOk")}
                        cancelText={t("navbar.cancel")}
                        placement="topRight"
                        onConfirm={onArchiveRead}
                        disabled={!hasRead}
                    >
                        <Button
                            type="link"
                            size="small"
                            icon={<Box size={16} />}
                            disabled={!hasRead}
                            loading={isArchivingRead}
                            className="p-0"
                        >
                            {t("navbar.archiveRead")}
                        </Button>
                    </Popconfirm>
                </footer>
            )}
        </section>
    );
};
